import React, { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowUpRight, ChevronLeft } from "lucide-react";
import { MaskedReveal, FadeUp } from "@/components/motion/Reveal";
import { PRACTICE_AREAS, FAQS } from "@/data/site";

function AccordionItem({ item, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t hairline">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full grid grid-cols-12 gap-4 py-6 text-left items-start"
        data-testid={`faq-item-${index}`}
      >
        <div className="col-span-1 mono text-xs text-gold">{String(index + 1).padStart(2, "0")}</div>
        <div className="col-span-10 serif text-xl md:text-2xl">{item.q}</div>
        <div className="col-span-1 justify-self-end">
          <motion.div animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.4 }}>
            <Plus className="h-5 w-5" />
          </motion.div>
        </div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.7, 0.05, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-12 gap-4 pb-8">
              <div className="col-span-1" />
              <div className="col-span-10 text-[15px] text-[hsl(var(--muted-foreground))] leading-relaxed max-w-2xl">{item.a}</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function PracticeArea() {
  const { slug } = useParams();
  const area = PRACTICE_AREAS.find(p => p.slug === slug);
  if (!area) return <Navigate to="/practice-areas" replace />;

  // pick 6 relevant FAQs (first 6 for now)
  const faqs = FAQS.slice(0, 6);

  return (
    <div data-testid={`pa-page-${slug}`}>
      {/* HERO */}
      <section className="relative min-h-[80vh] pt-40 pb-16 overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Link to="/practice-areas" className="editorial-link text-xs mono uppercase tracking-[0.28em] mb-8 inline-flex items-center gap-2">
            <ChevronLeft className="h-4 w-4" /> All Practice Areas
          </Link>
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
            <div className="col-span-12 md:col-span-8">
              <div className="mono text-xs uppercase tracking-[0.32em] text-gold mb-6">Practice · {area.title}</div>
              <MaskedReveal
                as="h1"
                className="serif text-6xl md:text-[8.5vw] leading-[0.9]"
                lines={[area.title.split(" ")[0], <em className="italic text-gold">{area.title.split(" ").slice(1).join(" ") || "Practice"}</em>]}
              />
              <FadeUp delay={0.4} className="mt-10 max-w-2xl text-[hsl(var(--muted-foreground))] text-[17px] leading-relaxed">
                {area.lede}
              </FadeUp>
            </div>
            <div className="col-span-12 md:col-span-4">
              <div className="aspect-[4/5] overflow-hidden hairline border">
                <img src={area.hero} alt={area.title} className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {area.highlight && (
        <section className="bg-navy text-ivory py-16">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-12 gap-6 items-center">
            <div className="col-span-12 md:col-span-6">
              <div className="mono text-xs uppercase tracking-[0.32em] text-gold mb-4">Urgent · 24×7</div>
              <h2 className="serif text-4xl md:text-5xl leading-tight">Bail Assistance — day or night.</h2>
            </div>
            <div className="col-span-12 md:col-span-6 md:text-right space-x-3">
              <a href="tel:+919920349263" className="btn-gold" data-testid="pa-bail-call">Call Now</a>
              <a href="https://wa.me/919920349263" target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm uppercase tracking-[0.18em] border border-white/50 text-[hsl(var(--ivory))] hover:bg-[hsl(var(--ivory))] hover:text-[hsl(var(--navy))] transition-colors" data-testid="pa-bail-wa">WhatsApp</a>
            </div>
          </div>
        </section>
      )}

      {/* SERVICES */}
      <section className="py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4">
            <div className="mono text-xs uppercase tracking-[0.32em] text-gold mb-4">Chapter 01 · Services</div>
            <MaskedReveal as="h2" className="serif text-4xl md:text-5xl leading-[0.98]" lines={["What we", <em className="italic text-gold">handle.</em>]} />
          </div>
          <div className="col-span-12 md:col-span-8">
            <ul className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:border-t hairline">
              {area.services.map((s, i) => (
                <FadeUp key={s} delay={i * 0.04}>
                  <li className="py-4 border-t hairline sm:border-t-0 sm:border-b flex items-start gap-3">
                    <span className="mono text-[10px] text-gold mt-1">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-[15px]">{s}</span>
                  </li>
                </FadeUp>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHO + APPROACH */}
      <section className="py-24 bg-[hsl(var(--ivory-deep))]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-6">
            <div className="mono text-xs uppercase tracking-[0.32em] text-gold mb-4">Chapter 02 · Who needs this</div>
            <h3 className="serif text-3xl md:text-4xl leading-snug">{area.who}</h3>
          </div>
          <div className="col-span-12 md:col-span-6">
            <div className="mono text-xs uppercase tracking-[0.32em] text-gold mb-4">Chapter 03 · Our approach</div>
            <p className="text-[17px] leading-relaxed">{area.approach}</p>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="mono text-xs uppercase tracking-[0.32em] text-gold mb-4">Chapter 04 · Legal process</div>
          <MaskedReveal as="h2" className="serif text-5xl md:text-6xl leading-[0.98] mb-16" lines={["From brief to", <em className="italic text-gold">judgment.</em>]} />
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {area.process.map((step, i) => (
              <FadeUp key={i} delay={i * 0.08} className="border-t hairline pt-4">
                <div className="chapter-num text-2xl">{String(i + 1).padStart(2, "0")}</div>
                <p className="mt-2 text-[15px] leading-snug">{step}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 bg-[hsl(var(--ivory-deep))]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="mono text-xs uppercase tracking-[0.32em] text-gold mb-4">Chapter 05 · FAQ</div>
          <MaskedReveal as="h2" className="serif text-5xl md:text-6xl leading-[0.98] mb-10" lines={["Questions,", <em className="italic text-gold">answered.</em>]} />
          <div>
            {faqs.map((f, i) => (
              <AccordionItem key={i} item={f} index={i} />
            ))}
          </div>
          <div className="mt-10">
            <Link to="/faq" className="editorial-link text-sm uppercase tracking-[0.22em]">All FAQs <ArrowUpRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 text-center">
          <MaskedReveal as="h2" className="serif text-5xl md:text-7xl leading-[0.95]" lines={[<>Ready to discuss your <em className="italic text-gold">matter?</em></>]} />
          <FadeUp delay={0.3} className="mt-10">
            <Link to="/contact" className="btn-ink" data-testid={`pa-book-${slug}`}>Book Consultation <ArrowUpRight className="h-4 w-4" /></Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
